<template>
    <el-dialog
        width="55vw"
        class="missed-call-handler-consent-dialog"
        top="15vh"
        :visible.sync="should_show_modal"
        :append-to-body="true"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :show-close="false"
    >
        <!-- Loading state -->
        <div v-if="loading_extra" class="loading-container">
            <i class="el-icon-loading"></i>
            <span>Loading...</span>
        </div>

        <!-- Content (only shown after extra data is loaded) -->
        <template v-else>
            <div slot="title" class="dialog-title">
                <strong v-if="missed_calls_count > 0">
                    You Missed {{ missed_calls_count }} Calls in {{ missed_calls_period_months }} Months — That's Lost Customers and Lost Cash!
                </strong>
                <strong v-else>
                    Never miss a call again!
                </strong>
            </div>
            
            <div class="consent-content">
                <!-- Main description -->
                <p class="consent-text mb-3">
                    Allow Aloware to answer ALL your missed calls by enabling AI Call Rescue.
                </p>
                
                <p class="consent-text mb-2">
                    This does the following:
                </p>
                
                <!-- Feature list -->
                <ul class="feature-list mb-3">
                    <li>Creates a new, custom AloAi Agent on your account, enabled by default</li>
                    <li>Sets the missed call settings for all Lines and Inboxes to be handled by this Agent</li>
                    <li>All calls handled by AI Call Rescue are automatically recorded and transcribed</li>
                    <li>Can be disabled and re-enabled at anytime, all existing missed call settings are preserved</li>
                </ul>
                
                <!-- Pricing -->
                <p class="consent-text mb-3">
                    AloAi Agents are billed at <strong class="price-text">${{ getBasicTierPrice() }}/min</strong> for basic voice models.
                </p>
            </div>

            <span slot="footer" class="dialog-footer">
                <el-button 
                    @click="handleDecision(false)"
                    :loading="loading"
                >
                    Not yet
                </el-button>
                <el-button 
                    type="primary" 
                    @click="handleDecision(true)"
                    :loading="loading"
                >
                    Accept & Enable
                </el-button>
            </span>
        </template>
    </el-dialog>
</template>

<script>
import axios from 'axios';
import { mapActions, mapState } from 'vuex';
import * as Consent from '../../constants/consent';

export default {
    name: 'MissedCallHandlerConsentDialog',
    
    props: {
        visible: {
            type: Boolean,
            default: false
        }
    },
    
    data() {
        return {
            loading: false,
            loading_extra: false,
            should_show_modal: false,
            missed_calls_count: 0,
            missed_calls_period_months: 6
        }
    },
    
    computed: {
        ...mapState('cache', ['current_company']),
    },
    
    watch: {
        visible(newVal) {
            this.should_show_modal = newVal
            if (newVal) {
                this.fetchMissedCallsData()
            }
        }
    },
    
    mounted() {
        this.should_show_modal = this.visible
        if (this.visible) {
            this.fetchMissedCallsData()
        }
    },
    
    methods: {
        ...mapActions('cache', ['setCurrentCompany']),
        
        async fetchMissedCallsData() {
            this.loading_extra = true
            try {
                const response = await axios.get(`/api/v1/consent/${Consent.CONSENT_TYPE_MISSED_CALL_HANDLER}/extra`)
                this.missed_calls_count = response.data.missed_calls_count || 0
                this.missed_calls_period_months = response.data.missed_calls_period_months || 6
            } catch (error) {
                console.error('Failed to fetch missed calls data:', error)
            } finally {
                this.loading_extra = false
            }
        },
        
        updateCurrentCompanyDetails() {
            return axios.get(`/api/v1/company/${this.current_company.id}`)
                .then(res => {
                    this.setCurrentCompany(res.data)
                })
        },
        
        getBasicTierPrice() {
            const company = this.current_company
            const rate = company?.rate
            
            if (rate?.aloai_voice_bot_basic_minute) {
                return parseFloat(rate.aloai_voice_bot_basic_minute).toFixed(2)
            }
            
            return '0.10'
        },
        
        async handleDecision(accepted) {
            this.loading = true
            
            try {
                const response = await axios.post(`/api/v1/consent/${Consent.CONSENT_TYPE_MISSED_CALL_HANDLER}`, {
                    accepted: accepted
                })
                
                this.should_show_modal = false
                this.$emit('update:visible', false)
                
                await this.updateCurrentCompanyDetails()
                
                if (accepted) {
                    this.$emit('consent-accepted')
                }
                
                this.$notify({
                    title: 'Consent',
                    message: accepted ? 'AI Call Rescue enabled for your account.' : 'AI Call Rescue remains off.',
                    type: accepted ? 'success' : 'info'
                })
                
            } catch (error) {
                this.$root.handleErrors(error.response)
            } finally {
                this.loading = false
            }
        }
    }
}
</script>

<style scoped>
.dialog-title {
    font-size: 18px;
    color: #303133;
}

.consent-content {
    max-height: 60vh;
    overflow-y: auto;
}

.consent-text {
    color: #000000;
    font-size: 14px;
    line-height: 1.5;
    margin: 0;
}

.feature-list {
    margin: 0;
    padding-left: 20px;
    font-size: 14px;
    line-height: 1.8;
    color: #000000;
}

.feature-list li {
    margin-bottom: 8px;
}

.feature-list li:last-child {
    margin-bottom: 0;
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.price-text {
    font-size: 14px !important;
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #909399;
    font-size: 14px;
}

.loading-container i {
    font-size: 32px;
    margin-bottom: 12px;
}
</style>
